import { Grid, useTheme, alpha } from '@mui/material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Legend,
} from 'recharts';
import { Group as GroupIcon } from '@mui/icons-material';
import { useI18nContext } from '@/i18n/i18n-react';
import type { MetricsData } from '@/types';
import {
    MetricsChartCard,
    MetricsSectionHeading,
    PIE_COLORS,
    renderPieLabel,
    makeTooltipStyle,
} from './index';

interface Props {
    data: MetricsData;
}

export const MetricsUsersSection = ({ data }: Props) => {
    const { LL } = useI18nContext();
    const theme = useTheme();
    const tt = makeTooltipStyle(theme);
    const divider = alpha(theme.palette.divider, 0.5);
    const tickStyle = { fill: theme.palette.text.secondary, fontSize: 12 };

    const userPieData = [
        { name: LL.metricsPage.pie.active(), value: data.users.active },
        { name: LL.metricsPage.pie.inactive(), value: data.users.total - data.users.active },
    ].filter((d) => d.value > 0);

    return (
        <MetricsSectionHeading
            icon={<GroupIcon fontSize="small" />}
            title={LL.metricsPage.sections.users()}
        >
            <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
                {/* User pie — always global */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <MetricsChartCard title={LL.metricsPage.charts.userBreakdown()}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={userPieData}
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={90}
                                    dataKey="value"
                                    label={renderPieLabel}
                                    labelLine={false}
                                >
                                    {userPieData.map((_, i) => (
                                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip {...tt} />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </MetricsChartCard>
                </Grid>

                {/* Roles bar — always global */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <MetricsChartCard title={LL.metricsPage.charts.userRoles()}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={[
                                    { name: LL.statsCards.admins(), value: data.users.admins },
                                    {
                                        name: LL.metricsPage.roles.scheduleEditors(),
                                        value: data.users.scheduleEditors,
                                    },
                                    {
                                        name: LL.metricsPage.roles.eventEditors(),
                                        value: data.users.eventEditors,
                                    },
                                ]}
                                layout="vertical"
                                margin={{ left: 20, right: 20 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke={divider} />
                                <XAxis type="number" allowDecimals={false} tick={tickStyle} />
                                <YAxis
                                    type="category"
                                    dataKey="name"
                                    tick={tickStyle}
                                    width={120}
                                />
                                <Tooltip {...tt} />
                                <Bar
                                    dataKey="value"
                                    fill={theme.palette.primary.main}
                                    radius={[0, 4, 4, 0]}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </MetricsChartCard>
                </Grid>

                {/* Top users by assignments */}
                {data.topUsers.length > 0 && (
                    <Grid size={{ xs: 12, md: 6 }}>
                        <MetricsChartCard
                            title={LL.metricsPage.charts.topUsersByAssignments()}
                            height={280}
                        >
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={data.topUsers}
                                    margin={{ left: 8, right: 16, top: 8 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke={divider} />
                                    <XAxis dataKey="name" tick={tickStyle} />
                                    <YAxis allowDecimals={false} tick={tickStyle} />
                                    <Tooltip {...tt} />
                                    <Bar
                                        dataKey="count"
                                        fill={theme.palette.secondary.main}
                                        radius={[4, 4, 0, 0]}
                                        name={LL.metricsPage.charts.assignmentsLabel()}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </MetricsChartCard>
                    </Grid>
                )}

                {/* Top users by login count */}
                {data.topUsersByLogin.length > 0 && (
                    <Grid size={{ xs: 12, md: 6 }}>
                        <MetricsChartCard
                            title={LL.metricsPage.charts.topUsersByLogins()}
                            height={280}
                        >
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={data.topUsersByLogin}
                                    margin={{ left: 8, right: 16, top: 8 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke={divider} />
                                    <XAxis dataKey="name" tick={tickStyle} />
                                    <YAxis allowDecimals={false} tick={tickStyle} />
                                    <Tooltip {...tt} />
                                    <Bar
                                        dataKey="count"
                                        fill={theme.palette.primary.main}
                                        radius={[4, 4, 0, 0]}
                                        name={LL.metricsPage.charts.loginsLabel()}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </MetricsChartCard>
                    </Grid>
                )}
            </Grid>
        </MetricsSectionHeading>
    );
};
